/* SIZESOFTX
=========================================================================================
* Descripción: Context del catálogo de Clientes
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 20/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 05/06/2026 | Lic. José Luis Aragón Cervantes  | Refresco de cliente al reasignar credencial
*      3       | 27/08/2026 | Lic. José Luis Aragón Cervantes  | Generación de QR de activación móvil
*      4       | 23/09/2026 | LIC. José Luis Aragón C.                       | Ortografía: caracteres reparados a UTF-8
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState, useEffect } from "react";
import QRCode from "qrcode";
import { useNavigate } from "react-router-dom";

import { isErrorForm, handleValidarStrings, getDatePost } from '../../settings/utils';
import { defaultTypes } from "../../settings/enums";
import {
    apiClientesList,
    apiClientesByNombre,
    apiClientesByClave,
    apiClientePorcentajeDescuento,
    apiClienteSave,
    apiClienteDelete,
    apiClienteAsignarTarjeta,
    apiClientePrefijoClave,
    apiClienteAppActivationQrCreate,
    serverClientesMoviles,
} from "../../settings/ApiConfig";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

const getStructCliente = () => ({
    fecharegistro: getDatePost(),
    clavecliente: defaultTypes.String,
    razonsocial: defaultTypes.String,
    calle: defaultTypes.String,
    numeroexterior: defaultTypes.String,
    numerointerior: defaultTypes.String,
    coloniapoblacion: defaultTypes.String,
    delegacionmunicipio: defaultTypes.String,
    ciudadpais: defaultTypes.String,
    cp: defaultTypes.String,
    telefono1: defaultTypes.String,
    rfc: defaultTypes.String,
    email: defaultTypes.String,
    contacto: defaultTypes.String,
    condicionespago: defaultTypes.String,
    porcentajedescuento: defaultTypes.double,
    colorcredencial: defaultTypes.String,
});

const getStructClientePayload = () => ({
    fecharegistro: { value: getDatePost(), error: false, helper: defaultTypes.String },
    clavecliente: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    razonsocial: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    calle: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    numeroexterior: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    numerointerior: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    coloniapoblacion: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    delegacionmunicipio: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    ciudadpais: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    cp: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    telefono1: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    rfc: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    email: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    contacto: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    condicionespago: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    porcentajedescuento: { value: 0.05, error: false, helper: defaultTypes.String },
    colorcredencial: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
});

const normalizeClienteText = (value, preserveCase = false) => {
    if (value === null || value === undefined) return "";
    const clean = String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\u0000-\u001F\u007F]/g, "")
        .trim();
    return preserveCase ? clean : clean.toUpperCase();
};

const normalizePrefijoClaveCliente = (value) => normalizeClienteText(value).replace(/[^A-Z]/g, "");
const normalizeClienteInput = (value, field = "") => {
    if (value === null || value === undefined) return "";
    const key = String(field).toLowerCase();
    if (["porcentajedescuento", "fecharegistro"].includes(key)) return value;
    return normalizeClienteText(value, false);
};

const validateClienteField = (name, value, requiredFields) => {
    const normalizedValue = normalizeClienteInput(value, name);
    const trimmed = String(normalizedValue ?? "").trim();
    const isRequired = requiredFields.includes(name);
    let error = false;
    let helper = "";

    if (isRequired && trimmed.length === 0) {
        error = true;
        helper = name === "razonsocial" ? "La Razon Social es requerida" : "El Contacto es requerido";
    } else if ((isRequired || trimmed.length > 0) && !["porcentajedescuento", "fecharegistro", "clavecliente"].includes(name)) {
        const mensajeError = handleValidarStrings(normalizedValue, name);
        if (mensajeError) {
            error = true;
            helper = mensajeError;
        }
    }

    if (!error && trimmed.length > 0) {
        const lower = trimmed.toLowerCase();
        if (
            lower.includes("<script") ||
            lower.includes("</script") ||
            lower.includes("javascript:") ||
            lower.includes("onerror=") ||
            lower.includes("onload=") ||
            lower.includes("onclick=") ||
            lower.includes("onmouseover=") ||
            lower.includes("onfocus=") ||
            lower.includes("onchange=") ||
            lower.includes("alert(") ||
            lower.includes("prompt(") ||
            lower.includes("confirm(") ||
            lower.includes("eval(") ||
            lower.includes("expression(")
        ) {
            error = true;
            helper = "Intento de inyeccion detectado y bloqueado en: (" + name + ")";
        }
    }

    return { value: normalizedValue, error, helper };
};
export const ClientesContext = createContext();
export const ClientesProvider = (props) => {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const navigation = useNavigate();

    const [clienteSeleccionado, setClienteSeleccionado] = useState(getStructCliente());
    const [statusOpenModalActivacionCliente, setStatusOpenModalActivacionCliente] = useState(false);
    const [clienteActivacionQr, setClienteActivacionQr] = useState(null);
    const [clienteActivacionQrText, setClienteActivacionQrText] = useState("");
    const [clienteActivacionQrDataUrl, setClienteActivacionQrDataUrl] = useState("");
    const [clientePayLoad, setClientePayLoad] = useState(getStructClientePayload());
    const [listaClientes, setListaClientes] = useState([]);
    const [statusEditCliente, setStatusEditCliente] = useState(true); // invertido para disabled
    const [statusEditableCliente, setStatusEditableCliente] = useState(false); // false = puede editar
    const [statusEnableFields, setStatusEnableFields] = useState(true); // true = campos deshabilitados
    const [statusBuscarCliente, setStatusBuscarCliente] = useState(false);
    const [statusOpenModalEliminarCliente, setStatusOpenModalEliminarCliente] = useState(false);
    const [statusOpenModalTarjeta, setStatusOpenModalTarjeta] = useState(false);
    const [statusOpenModalPrefijoClaveCliente, setStatusOpenModalPrefijoClaveCliente] = useState(false);
    const [statusOpenModalBuscarCliente, setStatusOpenModalBuscarCliente] = useState(false);
    const [codigoBarras, setCodigoBarras] = useState("");
    const [prefijoClaveCliente, setPrefijoClaveCliente] = useState("C");
    const [prefijoClaveClienteError, setPrefijoClaveClienteError] = useState(false);

    const handleClickRegresarMainDashboard = () => {
        navigation("/*");
    };

    const handleOpenModalBuscarCliente = () => {
        setStatusOpenModalBuscarCliente(true);
    };

    const handleCloseModalBuscarCliente = () => {
        setStatusOpenModalBuscarCliente(false);
    };

    const handleClienteSeleccionadoFromModal = (cliente) => {
        if (cliente && cliente.clavecliente) {
            setClienteSeleccionado(cliente);
            const payload = getStructClientePayload();
            Object.keys(payload).forEach((key) => {
                if (cliente[key] !== undefined) {
                    payload[key] = { value: cliente[key] ?? "", error: false, helper: "" };
                }
            });
            setClientePayLoad(payload);
            setStatusEditCliente(true); // Modo solo lectura
            setStatusEnableFields(true); // Campos deshabilitados
        }
    };

    // ---------------- Handlers de datos ----------------

    const handleChangeDataCliente = (event, newValue, fieldName) => {
        if (event?.target?.name) {
            const { name, value } = event.target;
            setClienteSeleccionado(prev => ({ ...prev, [name]: value }));
            return;
        }
        if (newValue !== null && fieldName) {
            const value = typeof newValue === "string" ? newValue : newValue?.[fieldName] || "";
            setClienteSeleccionado(prev => ({ ...prev, [fieldName]: value }));
        }
    };

    const REQUIRED_FIELDS = ['razonsocial', 'contacto'];

    const handleChangeDataClientePayload = (e, valor, field) => {
        let name = field ? field : e.target.name ?? "";
        let value = valor !== undefined ? valor : e.target.value ?? "";
        let error = false;
        let helper = "";

        const trimmed = String(value).trim();
        const isRequired = REQUIRED_FIELDS.includes(name);

        if (isRequired) {
            const mensajeError = handleValidarStrings(value, name);
            if (mensajeError) {
                error = true;
                helper = mensajeError;
            }
        } else if (trimmed.length > 0) {
            const mensajeError = handleValidarStrings(value, name);
            if (mensajeError) {
                error = true;
                helper = mensajeError;
            }
        }

        if (!error && trimmed.length > 0) {
            const lower = trimmed.toLowerCase();
            if (
                lower.includes("<script") ||
                lower.includes("</script") ||
                lower.includes("javascript:") ||
                lower.includes("onerror=") ||
                lower.includes("onload=") ||
                lower.includes("onclick=") ||
                lower.includes("onmouseover=") ||
                lower.includes("onfocus=") ||
                lower.includes("onchange=") ||
                lower.includes("alert(") ||
                lower.includes("prompt(") ||
                lower.includes("confirm(") ||
                lower.includes("eval(") ||
                lower.includes("expression(")
            ) {
                error = true;
                helper = "Intento de inyección detectado y bloqueado en: (" + name + ")";
            }
        }

        setClientePayLoad(prev => {
            const prevValue = prev[name]?.value ?? "";
            const newValueStr = String(value ?? "");
            const prevValueStr = String(prevValue ?? "");
            if (prevValueStr === newValueStr) return prev;
            return {
                ...prev,
                [name]: { ...prev[name], value, error, helper },
            };
        });
    };

    // ---------------- CRUD ----------------

    const handleObtenerClientesList = async () => {
        const result = await GetRequest({
            url: apiClientesList,
            loader: "Cargando lista de clientes...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al obtener la lista de clientes: " + result.message);
            return;
        }
        setListaClientes(result?.result ?? []);
    };

    const handleBuscarClientesByNombre = async (nombre) => {
        if (!nombre || nombre.trim().length === 0) return;
        const url = apiClientesByNombre.replace("{nombre}", nombre.trim());
        const result = await GetRequest({
            url,
            loader: "Buscando clientes por nombre...",
        });

        if (result.hasError) {
            handleOpenAlert("No se encontraron clientes con ese nombre");
            return [];
        }
        return result?.result ?? [];
    };

    const handleCargarClienteByClave = async (clave) => {
        if (!clave || clave.trim().length === 0) return;
        const url = apiClientesByClave.replace("{clave}", clave.trim().toUpperCase());
        const result = await GetRequest({
            url,
            loader: "Cargando datos del cliente...",
        });

        if (result.hasError) {
            handleOpenAlert("No se encontró el cliente con la clave indicada");
            return;
        }

        setClienteSeleccionado(result?.result);
        // NO cambiar statusEditCliente aquí - el useEffect lo maneja automáticamente
    };

    const handleObtenerPorcentajeDescuento = async (clave) => {
        if (!clave || clave.trim().length === 0) return 0;
        const url = apiClientePorcentajeDescuento.replace("{clave}", clave.trim().toUpperCase());
        const result = await GetRequest({
            url,
            loader: "Obteniendo porcentaje de descuento...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al obtener el porcentaje de descuento del cliente");
            return 0;
        }
        return result?.result ?? 0;
    };

    const handleClickNuevoCliente = () => {
        handleClickDeshacer();
        setStatusEditCliente(false);
        setStatusEnableFields(false); // Habilitar campos para nuevo cliente
    };

    const handleClickEditarCliente = () => {
        setStatusEditCliente(false);
        setStatusEnableFields(false); // Habilitar campos para edición
    };

    const handleClickDeshacer = () => {
        setClienteSeleccionado(getStructCliente());
        setClientePayLoad(getStructClientePayload());
        setStatusEditCliente(true);
        setStatusEnableFields(true); // Deshabilitar campos
    };

    const handleClickGuardarCliente = async () => {
        const nextPayload = {};
        const resultPayload = {};
        let hasErrors = false;

        Object.entries(clientePayLoad).forEach(([key, meta]) => {
            const rawValue = meta && typeof meta === "object" && "value" in meta ? meta.value : meta;
            const validation = validateClienteField(key, rawValue, REQUIRED_FIELDS);
            nextPayload[key] = meta && typeof meta === "object" && "value" in meta
                ? { ...meta, ...validation }
                : validation;
            resultPayload[key] = validation.value;
            if (validation.error) hasErrors = true;
        });

        setClientePayLoad(nextPayload);

        if (hasErrors || isErrorForm(nextPayload)) {
            handleOpenAlert("Existen errores en los datos del cliente. Revise los campos marcados antes de guardar.", "error");
            return;
        }
        const result = await PostRequest({
            url: apiClienteSave,
            body: resultPayload,
            loader: "Guardando datos del cliente...",
        });

        if (result.hasError || result.errors) {
            handleOpenAlert("Error al guardar datos del cliente: " + result.message);
            return;
        }

        handleOpenAlert("Cliente guardado correctamente", "ok");
        setStatusEditCliente(true);
        setStatusEnableFields(true); // Deshabilitar campos
        
        // Si el backend devuelve el cliente con clave generada, usarla
        if (result.result && result.result.clavecliente) {
            setClienteSeleccionado(result.result);
            const payload = getStructClientePayload();
            Object.keys(payload).forEach((key) => {
                if (result.result[key] !== undefined) {
                    payload[key] = { value: result.result[key] ?? "", error: false, helper: "" };
                }
            });
            setClientePayLoad(payload);
        } else {
            setClienteSeleccionado(resultPayload);
        }
        
        await handleObtenerClientesList();
    };

    const handleClickEliminarCliente = async () => {
        if (!clienteSeleccionado.razonsocial || clienteSeleccionado.razonsocial.trim().length === 0) return;

        const url = apiClienteDelete.replace("{razonsocial}", encodeURIComponent(clienteSeleccionado.razonsocial.trim()));

        const result = await PostRequest({
            url,
            loader: "Eliminando cliente del sistema...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al eliminar el cliente: " + result.message);
            return;
        }

        handleOpenAlert("Cliente eliminado correctamente", "ok");
        setStatusOpenModalEliminarCliente(false);
        handleClickDeshacer();
        await handleObtenerClientesList();
    };

    const handleClickRefrescarDatos = async () => {
        await handleObtenerClientesList();
        handleOpenAlert("Datos actualizados correctamente", "ok");
    };

    const handleClickAsignarTarjeta = () => {
        setCodigoBarras("");
        setStatusOpenModalTarjeta(true);
    };

    const handleCloseModalActivacionCliente = () => {
        setStatusOpenModalActivacionCliente(false);
    };

    const handleGenerarQrActivacionCliente = async () => {
        const claveCliente = normalizeClienteText(clienteSeleccionado?.clavecliente);
        if (!claveCliente) {
            handleOpenAlert("Seleccione un cliente con clave antes de generar la activación móvil", "warning");
            return;
        }

        const result = await PostRequest({
            url: apiClienteAppActivationQrCreate,
            body: {
                claveCliente,
                mode: "original",
                apiUrl: serverClientesMoviles,
            },
            loader: "Generando QR de activación móvil...",
        });

        if (result.hasError) {
            handleOpenAlert("No se pudo generar el QR de activación: " + result.message, "error");
            return;
        }

        const qrResult = result.result ?? {};
        const qrPayload = {
            activationToken: qrResult.activationToken,
            apiUrl: qrResult.apiUrl || serverClientesMoviles,
            mode: qrResult.mode || "original",
        };
        const qrText = JSON.stringify(qrPayload);
        const qrDataUrl = await QRCode.toDataURL(qrText, {
            width: 320,
            margin: 2,
            errorCorrectionLevel: "M",
        });

        setClienteActivacionQr(clienteSeleccionado);
        setClienteActivacionQrText(qrText);
        setClienteActivacionQrDataUrl(qrDataUrl);
        setStatusOpenModalActivacionCliente(true);
    };

    const handleConfirmarAsignacionTarjeta = async () => {
        if (!codigoBarras.trim() || !clienteSeleccionado.clavecliente) return;

        const body = {
            claveClienteActual: normalizeClienteText(clienteSeleccionado.clavecliente),
            nuevoCodigoBarras: normalizeClienteText(codigoBarras),
        };

        const result = await PostRequest({
            url: apiClienteAsignarTarjeta,
            body,
            loader: "Asignando tarjeta al cliente...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al asignar la tarjeta: " + result.message);
            return;
        }

        setStatusOpenModalTarjeta(false);
        setCodigoBarras("");

        const nuevaClave = result.result?.clavecliente ?? normalizeClienteText(codigoBarras);
        const clienteActualizado = result.result && typeof result.result === "object"
            ? result.result
            : { ...clienteSeleccionado, clavecliente: nuevaClave };

        setClienteSeleccionado(clienteActualizado);
        const payload = getStructClientePayload();
        Object.keys(payload).forEach((key) => {
            if (clienteActualizado[key] !== undefined) {
                payload[key] = { value: clienteActualizado[key] ?? "", error: false, helper: "" };
            }
        });
        setClientePayLoad(payload);
        setStatusEditCliente(true);
        setStatusEnableFields(true);
        handleOpenAlert(`Tarjeta asignada correctamente. Nueva clave: ${nuevaClave}`, "ok");

        await handleObtenerClientesList();
    };

    // ---------------- Modales ----------------


    const handleOpenModalPrefijoClaveCliente = async () => {
        const result = await GetRequest({
            url: apiClientePrefijoClave,
            loader: "Obteniendo prefijo de claves de clientes...",
        });

        if (result.hasError) {
            handleOpenAlert("No se pudo obtener el prefijo de claves de clientes: " + result.message, "error");
            return;
        }

        const prefijo = normalizePrefijoClaveCliente(result?.result?.prefijoclavecliente ?? result?.result ?? "C") || "C";
        setPrefijoClaveCliente(prefijo);
        setPrefijoClaveClienteError(false);
        setStatusOpenModalPrefijoClaveCliente(true);
    };

    const handleCloseModalPrefijoClaveCliente = () => {
        setStatusOpenModalPrefijoClaveCliente(false);
        setPrefijoClaveClienteError(false);
    };

    const handleChangePrefijoClaveCliente = (value) => {
        const prefijo = normalizePrefijoClaveCliente(value);
        setPrefijoClaveCliente(prefijo);
        setPrefijoClaveClienteError(prefijo.length === 0);
    };

    const handleConfirmarPrefijoClaveCliente = async () => {
        const prefijo = normalizePrefijoClaveCliente(prefijoClaveCliente);
        if (!prefijo) {
            setPrefijoClaveClienteError(true);
            handleOpenAlert("El prefijo debe contener al menos una letra", "error");
            return;
        }

        const result = await PostRequest({
            url: apiClientePrefijoClave,
            body: { prefijoclavecliente: prefijo },
            loader: "Guardando prefijo de claves de clientes...",
        });

        if (result.hasError) {
            handleOpenAlert("No se pudo guardar el prefijo de claves de clientes: " + result.message, "error");
            return;
        }

        const prefijoGuardado = normalizePrefijoClaveCliente(result?.result?.prefijoclavecliente ?? prefijo) || prefijo;
        setPrefijoClaveCliente(prefijoGuardado);
        setPrefijoClaveClienteError(false);
        setStatusOpenModalPrefijoClaveCliente(false);
        handleOpenAlert(`Prefijo de claves de clientes actualizado: ${prefijoGuardado}`, "ok");
    };
    return (
        <ClientesContext.Provider
            value={{
                clienteSeleccionado, setClienteSeleccionado,
                clientePayLoad, setClientePayLoad, getStructClientePayload,
                listaClientes,
                statusEditCliente, setStatusEditCliente,
                statusEditableCliente, setStatusEditableCliente,
                statusEnableFields,
                statusBuscarCliente,
                statusOpenModalEliminarCliente, setStatusOpenModalEliminarCliente,
                statusOpenModalTarjeta, setStatusOpenModalTarjeta,
                statusOpenModalBuscarCliente, setStatusOpenModalBuscarCliente,
                codigoBarras, setCodigoBarras,
                handleClickRegresarMainDashboard,
                handleOpenModalBuscarCliente,
                handleCloseModalBuscarCliente,
                handleClienteSeleccionadoFromModal,
                handleChangeDataCliente,
                handleChangeDataClientePayload,
                handleObtenerClientesList,
                handleBuscarClientesByNombre,
                handleCargarClienteByClave,
                handleObtenerPorcentajeDescuento,
                handleClickNuevoCliente,
                handleClickEditarCliente,
                handleClickDeshacer,
                handleClickGuardarCliente,
                handleClickEliminarCliente,
                handleClickRefrescarDatos,
                handleClickAsignarTarjeta,
                handleConfirmarAsignacionTarjeta,
                statusOpenModalPrefijoClaveCliente,
                prefijoClaveCliente,
                prefijoClaveClienteError,
                handleOpenModalPrefijoClaveCliente,
                handleCloseModalPrefijoClaveCliente,
                handleChangePrefijoClaveCliente,
                handleConfirmarPrefijoClaveCliente,
                statusOpenModalActivacionCliente,
                setStatusOpenModalActivacionCliente,
                clienteActivacionQr,
                clienteActivacionQrText,
                clienteActivacionQrDataUrl,
                handleGenerarQrActivacionCliente,
                handleCloseModalActivacionCliente,
            }}
        >
            {props.children}
        </ClientesContext.Provider>
    );
};

export const useClientes = () => useContext(ClientesContext);







